import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Alert, Modal, ModalBody, ModalHeader } from 'reactstrap';
import { ArtistPortalArtistRef } from 'fym-shared/src/smartlinks/artistPortals';
import { TypedMemo } from '../../../common/types';
import { LoadingView } from '../../../common/views/LoadingView';
import { ErrorAlert } from '../../../common/views/ErrorAlert/ErrorAlert';
import { ServiceConnection } from '../../../../redux/api/fymAPI.types';
import { ArtistPortalArtistPicker } from '../ArtistPortalArtistPicker';
import { useArtistPortalConnect } from '../useArtistPortalConnect';
import { sessionCaptureMessages as messages } from './sessionCaptureMessages';

type PortalActions = Pick<
  ReturnType<typeof useArtistPortalConnect>,
  'loadArtists' | 'selectArtist' | 'isBusy' | 'error'
>;

interface SessionCaptureArtistModalProps {
  /** The card the picker is for. Undefined closes the modal. */
  connection?: ServiceConnection;
  portal: PortalActions;
  /** The pick is saved and the read started. */
  onPicked: () => void;
  onClose: () => void;
}

/**
 * "Choose artist" and "Change artist" of a login that runs inside the Music24 app (Ditto Music).
 *
 * The login is stored on the server, so the web can pick: it asks the server for the artists of
 * the login (`GET …/serviceConnections/ditto/artists`) and saves the pick
 * (`POST …/serviceConnections/ditto/artist`), which starts a read. A change of artist never mixes
 * two artists: Streaming shows the numbers of the artist of today, and the numbers of the artist
 * from before stay stored under that artist.
 */
const SessionCaptureArtistModalComponent: React.FC<SessionCaptureArtistModalProps> = ({
  connection,
  portal,
  onPicked,
  onClose,
}) => {
  const intl = useIntl();
  const [artists, setArtists] = useState<ArtistPortalArtistRef[]>();
  const { loadArtists, selectArtist } = portal;
  const integrationType = connection?.integrationType;

  // A new list for each open: the list is only worth what the login can read now.
  useEffect(() => {
    setArtists(undefined);
    if (integrationType === undefined) {
      return;
    }
    let isCurrent = true;
    loadArtists(integrationType)
      .then((loaded) => {
        if (isCurrent) {
          setArtists(loaded ?? []);
        }
      })
      .catch(console.error);
    return () => {
      isCurrent = false;
    };
  }, [integrationType, loadArtists]);

  const pick = useCallback(
    (externalArtistId: string) => {
      if (integrationType === undefined) {
        return;
      }
      selectArtist(integrationType, externalArtistId)
        .then((saved) => {
          if (saved) {
            onPicked();
          }
        })
        .catch(console.error);
    },
    [integrationType, onPicked, selectArtist]
  );

  if (connection === undefined) {
    return null;
  }
  const current = connection.artist;
  const isChange = current !== null;

  let body: React.ReactNode;
  if (artists === undefined) {
    body = <LoadingView />;
  } else if (artists.length === 0) {
    body =
      portal.error === undefined ? (
        <Alert color="info" className="mb-0">
          {intl.formatMessage(messages.noArtistsOnLogin)}
        </Alert>
      ) : null;
  } else {
    body = (
      <ArtistPortalArtistPicker
        artists={artists}
        isLoading={portal.isBusy}
        onSelect={pick}
        currentExternalId={current?.externalId ?? null}
        help={
          isChange
            ? intl.formatMessage(messages.changeArtistHelp, { artist: current.name })
            : intl.formatMessage(messages.chooseArtistHelp)
        }
      />
    );
  }

  return (
    <Modal isOpen toggle={onClose} centered className="sml-modal">
      <ModalHeader toggle={onClose}>
        {intl.formatMessage(isChange ? messages.changeArtistTitle : messages.chooseArtistTitle)}
      </ModalHeader>
      <ModalBody>
        {portal.error !== undefined && <ErrorAlert error={portal.error} />}
        {body}
      </ModalBody>
    </Modal>
  );
};

export const SessionCaptureArtistModal = TypedMemo(SessionCaptureArtistModalComponent);
